import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Mail } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { api, apiError } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';
import { UsersPage } from './UsersPage';
import { AuditLogPage } from './AuditLogPage';
import { FolderTemplatesPage } from './FolderTemplatesPage';
import { RequestTemplatesPage } from './RequestTemplatesPage';

interface SmtpConfig {
  configured: boolean;
  host: string | null;
  port: number;
  from_email: string | null;
  use_tls: boolean;
  use_ssl: boolean | null;
}

interface SystemSettings {
  environment: string;
  max_upload_mb: number;
  storage_root: string;
  cors_origins: string[];
  jwt_access_minutes: number;
  jwt_refresh_days: number;
  smtp: SmtpConfig;
}

interface TestResult {
  status: 'sent' | 'skipped' | 'failed';
  reason?: string;
  error?: string;
  error_class?: string;
  trace?: string;
  to?: string;
  from_email?: string;
  host?: string;
  port?: number;
}

function useSystemSettings() {
  return useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: async () => (await api.get<SystemSettings>('/admin/settings')).data,
  });
}

type Tab = 'system' | 'users' | 'folder-templates' | 'request-templates' | 'audit';

const TABS: { id: Tab; label: string }[] = [
  { id: 'system', label: 'System' },
  { id: 'users', label: 'Users' },
  { id: 'folder-templates', label: 'Folder templates' },
  { id: 'request-templates', label: 'Request templates' },
  { id: 'audit', label: 'Audit log' },
];

export function SystemSettingsPage() {
  usePageTitle('Settings');
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') ?? 'system') as Tab;

  const setTab = (t: Tab) => setParams({ tab: t }, { replace: true });

  return (
    <div className="space-y-4">
      <div className="flex gap-0 border-b border-slate-200 -mb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
              tab === t.id
                ? 'border-bb-navy text-bb-navy'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="pt-1">
        {tab === 'system' && <SystemContent />}
        {tab === 'users' && <UsersPage staffOnly />}
        {tab === 'folder-templates' && <FolderTemplatesPage />}
        {tab === 'request-templates' && <RequestTemplatesPage />}
        {tab === 'audit' && <AuditLogPage />}
      </div>
    </div>
  );
}

function SystemContent() {
  const { user } = useAuth();
  const settings = useSystemSettings();
  const [testEmail, setTestEmail] = useState(user?.email ?? '');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);

  const runTest = async () => {
    if (!testEmail.trim()) return;
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post<TestResult>('/admin/smtp-test', {
        to: testEmail.trim(),
      });
      setResult(data);
    } catch (e) {
      setResult({
        status: 'failed',
        error: apiError(e),
        error_class: 'Request failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const smtp = settings.data?.smtp;

  return (
    <div className="space-y-4">
      <Card title="SMTP / outbound email">
        <p className="text-xs text-slate-500 mb-4">
          Password reset links, user invitations, and important notifications go out
          over SMTP. Leaving host or from-address blank disables delivery — the
          backend logs skipped messages without failing. Use the test button below
          to verify your config end-to-end.
        </p>

        {settings.isLoading ? (
          <p className="text-sm text-slate-500">Loading current config…</p>
        ) : smtp ? (
          <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm mb-5">
            <dt className="text-slate-500">Status</dt>
            <dd>
              {smtp.configured ? (
                <Badge tone="green">Configured</Badge>
              ) : (
                <Badge tone="yellow">Not configured</Badge>
              )}
            </dd>

            <dt className="text-slate-500">Host</dt>
            <dd className="text-slate-800 font-mono text-xs">
              {smtp.host ?? <span className="text-slate-400 font-sans not-italic">—</span>}
            </dd>

            <dt className="text-slate-500">Port</dt>
            <dd className="text-slate-800">{smtp.port}</dd>

            <dt className="text-slate-500">Display name</dt>
            <dd className="text-slate-800 text-xs">BillerBay Exchange</dd>

            <dt className="text-slate-500">From address</dt>
            <dd className="text-slate-800 font-mono text-xs">
              {smtp.from_email
                ? (smtp.from_email.match(/<([^>]+)>/)?.[1] ?? smtp.from_email)
                : <span className="text-slate-400 font-sans">—</span>}
            </dd>

            <dt className="text-slate-500">Transport</dt>
            <dd className="text-slate-800">
              {deriveTransport(smtp)}
            </dd>
          </dl>
        ) : null}

        <div className="border-t border-slate-100 pt-4">
          <Label htmlFor="smtp-test-to">Send a test email to</Label>
          <div className="flex flex-col sm:flex-row gap-2 mt-1">
            <Input
              id="smtp-test-to"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1"
            />
            <Button
              variant="primary"
              onClick={runTest}
              loading={testing}
              disabled={!testEmail.trim() || !smtp?.configured}
              className="w-auto"
            >
              Send test
            </Button>
          </div>
          {!smtp?.configured && (
            <p className="text-xs text-slate-500 mt-1">
              Set SMTP_HOST and SMTP_FROM_EMAIL in deploy/.env first, then redeploy.
            </p>
          )}
        </div>

        {result && <TestResultPanel result={result} />}
      </Card>

      <Card title="Other settings">
        {settings.data ? (
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-sm">
            <dt className="text-slate-500">Environment</dt>
            <dd className="text-slate-800">{settings.data.environment}</dd>

            <dt className="text-slate-500">Max upload size</dt>
            <dd className="text-slate-800">{settings.data.max_upload_mb} MB</dd>

            <dt className="text-slate-500">Storage root</dt>
            <dd className="text-slate-800 font-mono text-xs">{settings.data.storage_root}</dd>

            <dt className="text-slate-500">Allowed origins (CORS)</dt>
            <dd className="text-slate-800 font-mono text-xs break-all">
              {settings.data.cors_origins.join(', ')}
            </dd>

            <dt className="text-slate-500">Access token lifetime</dt>
            <dd className="text-slate-800">{settings.data.jwt_access_minutes} min</dd>

            <dt className="text-slate-500">Refresh token lifetime</dt>
            <dd className="text-slate-800">{settings.data.jwt_refresh_days} days</dd>
          </dl>
        ) : (
          <p className="text-sm text-slate-500">Loading…</p>
        )}
      </Card>
    </div>
  );
}

function deriveTransport(smtp: SmtpConfig): string {
  if (smtp.use_ssl === true) return 'Implicit TLS (SMTPS)';
  if (smtp.use_ssl === false) {
    return smtp.use_tls ? 'Plain + STARTTLS' : 'Plain (no encryption)';
  }
  if (smtp.port === 465) return 'Implicit TLS (SMTPS) — auto-detected from port 465';
  return smtp.use_tls ? 'Plain + STARTTLS' : 'Plain (no encryption)';
}

function TestResultPanel({ result }: { result: TestResult }) {
  if (result.status === 'sent') {
    return (
      <div className="mt-3 bg-green-50 border border-green-200 p-3 text-sm">
        <div className="flex items-center gap-2 font-semibold text-green-800">
          <CheckCircle2 size={16} /> Test email sent
        </div>
        <p className="text-xs text-green-700 mt-1">
          Delivered from {result.from_email} via {result.host}:{result.port} to {result.to}.
          Check the recipient's inbox (including spam) in the next minute.
        </p>
      </div>
    );
  }
  if (result.status === 'skipped') {
    return (
      <div className="mt-3 bg-yellow-50 border border-yellow-200 p-3 text-sm">
        <div className="flex items-center gap-2 font-semibold text-yellow-800">
          <Mail size={16} /> Skipped
        </div>
        <p className="text-xs text-yellow-800 mt-1">{result.reason}</p>
      </div>
    );
  }
  return (
    <div className="mt-3 bg-red-50 border border-red-200 p-3 text-sm">
      <div className="flex items-center gap-2 font-semibold text-red-800">
        <XCircle size={16} /> Failed
      </div>
      <div className="text-xs text-red-800 mt-1">
        <div>
          <span className="font-semibold">{result.error_class}</span>: {result.error}
        </div>
        {result.host && (
          <div className="mt-1 font-mono">
            host={result.host} port={result.port}
          </div>
        )}
      </div>
      {result.trace && (
        <pre className="text-[10px] bg-white border border-red-200 p-2 mt-2 overflow-x-auto custom-scrollbar">
          {result.trace}
        </pre>
      )}
      <p className="text-xs text-red-800 mt-2">
        Common causes: wrong SMTP_USE_SSL/SMTP_USE_TLS for your port, invalid
        credentials, From address not verified with your provider, or firewall
        blocking the port outbound from the VM.
      </p>
    </div>
  );
}
